/*
 * File: c:\Users\j.mazat\Desktop\Autobuy\AutobuyBackOffice\src\Content\Message\Message.tsx
 * Project: c:\Users\j.mazat\Desktop\Autobuy\AutobuyBackOffice
 * Created Date: Tuesday August 1st 2023
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Thursday, 9th November 2023 04:28:44
 * Modified By: Simon (s.gourlet@autobuy.fr>)
 * -----
 * Copyright (c) 2023 Autobuy SAS
 * -----
 */

import { useEffect, useState } from "react";
import Styles from "Content/Message/Message.module.css";
import TinyEditor from "Content/TinyMCE/TinyEditor";
import { useLoaderData } from "react-router-dom";
import translator from "MultiLanguage/TranslationWrapper";
import { MessageType } from "Type/message.type";
import TitlePage from "Content/HeadingPage/TitlePage/TitlePage";
import RouterPathResolver from "Config/Router/RouterPathResolver";
import MessageService from "Services/Message/MessageService";
import ModalConfirmation from "Content/Modals/ModalConfirmation/ModalConfirmation";
import LiCustom from "Content/LiCustom/LiCustom";
import SelectCommercial from "Content/User/Commercials/SelectCommercial";
import { UserData, UserSimple } from "Type/user.type";
import { ModalConfirmRequest } from "Content/Modals/ModalConfirmRequest/ModalConfirmRequest";

function SendMessage() {
    const loadData = useLoaderData() as [MessageType[], any];
    const [users, setUsers] = useState<{myUsers: UserData[], nb_page: number}>({myUsers: [], nb_page:0});
    const [usersFiltered, setUsersFiltered] = useState<UserData[]>([]);
    const [buttonEnable, setButtonEnable] = useState<Boolean>(true);
    const [commercial, setCommercial] = useState<UserSimple>();
    const [myTrueData, setMyTrueData] = useState<MessageType[] | null>(loadData[0].length !== 0 ? [...loadData[0]] : null);
    const [messageName, setMessageName] = useState<string | null>(loadData[0].length !== 0 ? loadData[0][0].Name : null);
    const [messageID, setMessageID] = useState<string | null>(loadData[0].length !== 0 ? loadData[0][0].id : null);
    const [messageContent, setMessageContent] = useState<string>(loadData[0].length !== 0 && loadData[0][0].Content ? loadData[0][0].Content : "");
    const [messageToDisplay, setMessageToDisplay] = useState<string>(loadData[0].length !== 0 && loadData[0][0].Content ? loadData[0][0].Content : "");
    const [showConfirmRequestModal, setShowConfirmRequestModal] = useState(false);
    const [newTemplate, setNewTemplate] = useState<MessageType | undefined>();
    const [isOkay, setIsOkay] = useState<boolean>(false);
    const [confirmModal, setConfirmModal] = useState<boolean>(false);
    const [dataUpdated, setDataUpdated] = useState<string | null>(null);

    useEffect(() => {
        Promise.all(loadData).then((values: {myUsers: [], nb_page: number}[]) => {
            setUsers(values[1]);
        });
    }, [loadData]);

    useEffect(() => {
        setUsersFiltered(users.myUsers.filter(user => (commercial?.Email ? user.CommercialID === commercial?.id : true) && user.CommercialCallTime));
        if (commercial && messageToDisplay) {
            const newMessage = messageContent
                .replace(/{{CommercialFirstName}}/g, commercial.FirstName ? commercial.FirstName[0].toUpperCase() + commercial.FirstName.slice(1) : "")
                .replace(/{{CommercialLastName}}/g, commercial.LastName?.toUpperCase() ?? "")
                .replace(/{{CommercialEmail}}/g, commercial.Email ?? "")
                .replace(/{{CommercialCellPhone}}/g, commercial.CellPhone ?? "");
            setMessageToDisplay(newMessage);
        }
    }, [commercial]);

    useEffect(() => {
        if (newTemplate) {
            setMessageName(newTemplate.Name);
            setMessageID(newTemplate.id);
            setMessageToDisplay(newTemplate.Content ?? "");
            setDataUpdated(null);
            if(myTrueData === null) {
                setMyTrueData([newTemplate]);
            } else {
                setMyTrueData([...myTrueData, newTemplate]);
            }
            setNewTemplate(undefined);
        }
    }, [newTemplate]);

    useEffect(() => {
        if (dataUpdated != null && myTrueData) {
            let idxToDel: number | undefined;
            myTrueData.forEach((item, index) => { if (item.Name === messageName) {idxToDel = index} });
            if (idxToDel) {
                myTrueData[idxToDel].Content = dataUpdated;
                setDataUpdated(null);
                setMyTrueData([...myTrueData]);
            }
        }
    }, [dataUpdated]);

    const handleDataToDisplay = (messageName :string) => {
        let tmp = myTrueData?.filter((val) => val.Name === messageName);
        if (tmp && tmp.length > 0 && messageID !== tmp[0].id) {
            setMessageName(tmp[0].Name);
            setMessageID(tmp[0].id);
            if (tmp[0].Content) {
                setMessageContent(tmp[0].Content);
                if (commercial) {
                    const newMessage = tmp[0].Content
                        .replace(/{{CommercialFirstName}}/g, commercial.FirstName ? commercial.FirstName[0].toUpperCase() + commercial.FirstName.slice(1) : "")
                        .replace(/{{CommercialLastName}}/g, commercial.LastName?.toUpperCase() ?? "")
                        .replace(/{{CommercialEmail}}/g, commercial.Email ?? "")
                        .replace(/{{CommercialCellPhone}}/g, commercial.CellPhone ?? "");
                    setMessageToDisplay(newMessage);
                } else {
                    setMessageToDisplay(tmp[0].Content);
                }
            } else {
                setMessageToDisplay("");
                setMessageContent("");
            }
        }
    };

    const closeConfirmRequestModal = () => {
        setShowConfirmRequestModal(false);
    };

    const closeConfirmModal = () => {
        setConfirmModal(false);
        setShowConfirmRequestModal(false);
    }

    const manageSetter = (IsOkay: boolean, OpenModal: boolean, ButtonEnable: boolean) => {
        setIsOkay(IsOkay);
        setConfirmModal(OpenModal);
        setButtonEnable(ButtonEnable);
    };

    const handleSendMessage = () => {
        if (!messageID)
            return;
        setButtonEnable(false);
        MessageService.sendMessage(messageID, messageToDisplay, usersFiltered.map(user => user.Email), !commercial?.Email).then(() => {
            manageSetter(true, true, true);
        }).catch(() => {
            manageSetter(false, true, true);
        });
    }

    return(
        <div className="p-3">
            <TitlePage title={translator.decode("dynamic.sendMessage")} backLink={RouterPathResolver.pathHome.path} />
            <div className={`${Styles.messageHolder}`}>
                <LiCustom data={myTrueData ? myTrueData?.map(val => val.Name).sort((v1: string, v2: string) => v1.localeCompare(v2)) : null} onClick={handleDataToDisplay} title={translator.decode("dynamic.message")} />
                <TinyEditor disabled={true} handleSaveMessage={() => {}} displayMenuBar={false} messageToDisplay={messageToDisplay} messageName={messageName} id={messageID} deleteMessage={() => {}} setDataUpdated={setDataUpdated} />
                <div className={`${Styles.rightSection}`}>
                    <SelectCommercial setNewCommercial={setCommercial} valueName="value" baseValue="Tous les commerciaux" onUpdate={() => { } } className={Styles.select} filteredCommercialID={""} label={translator.decode("dynamic.commercial")} />
                    <p><strong>{usersFiltered.length} </strong>Clients sélectionnés</p>
                    <button disabled={!buttonEnable || usersFiltered.length === 0} className="btn btn-outline-success" onClick={(e) => {e.stopPropagation(); setShowConfirmRequestModal(true)}}>{translator.decode("dynamic.send")}</button>
                </div>
            </div>
            <ModalConfirmation show={confirmModal} onClose={closeConfirmModal} closeModal={closeConfirmModal} isOkay={isOkay} />
            <ModalConfirmRequest show={showConfirmRequestModal} closeModal={closeConfirmRequestModal} title={translator.decode(`dynamic.send`)} onClose={closeConfirmRequestModal} additionnalMessage={translator.decode(`dynamic.sendMessageUsersCommercial`).replace('%c', usersFiltered.length).replace('%nc', commercial?.LastName)} request={handleSendMessage} />
        </div>
    )
}

export default SendMessage;
